import './Graphic.scss';
import * as THREE from 'three';
import React, { useEffect, useRef } from 'react';
import graphicLegend from '../../../images/schermata_di_calcolo.png';
import steelGridMesh from '../../../images/mesh_steelgrid.png';
import heaMesh from '../../../images/mesh_hea.png'; 
import dtMesh from '../../../images/mesh_dt.png';
import macArmourMesh from '../../../images/mesh_macarmour.png';
import macringMesh from '../../../images/RINGS -white.png';
import chiodoImg from '../../../images/chiodo.png';
import chiodoConBarraImg from '../../../images/chiodo_con_barra.png';
import gridImage from '../../../images/grid.svg';
import streetTextureImg from '../../../images/street_texture.jpg';
import wallTexture from '../../../images/texture.jpg';
import groundTexture from '../../../images/grey_rock_ground.jpg';
import { Grid } from '@mui/material';
import { IGraphicProps } from './IGraphic';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RootState } from '../../../../reduxService';
import { useSelector } from 'react-redux';
import {setDebrisAccumulationAngle} from '../../store/project/projectslice';
import { useAppDispatch } from '../../../../reduxService/hooks';
import * as TWEEN from '@tweenjs/tween.js';
import { useLocation } from 'react-router-dom';

export const Graphic = (props: IGraphicProps) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene>();
    const cameraRef = useRef<THREE.PerspectiveCamera>();
    const rendererRef = useRef<THREE.WebGLRenderer>();
    const [img, setImg] = React.useState('');
    const location = useLocation();

    const committedProject = useSelector((state: RootState) => state.Macro2.committedProject);

    const inResults = location.pathname.split('/')[1] === 'results';

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Initialize Three.js components when the component mounts
        if (!mountRef.current) return;

        let slopeTotalHeight = 0;
        let debrisAccumulationHeight = 0;
        let slopeInclination = 0;
        let debrisAccumulationWidth = 0;
        let horizontalSpacing = 0;


        //console.log(committedProject.data);

        if(committedProject.data && committedProject.data.rockSlope) {
            slopeInclination = committedProject.data.rockSlope.inclination;
            slopeTotalHeight = committedProject.data.rockSlope.slopeTotalHeight;
            debrisAccumulationHeight = committedProject.data.rockSlope.debrisAccumulationHeight;
            debrisAccumulationWidth = committedProject.data.rockSlope.debrisAccumulationWidth;
        }

        if(committedProject.data && committedProject.data.crest) {
            horizontalSpacing = committedProject.data.crest.horizontalAnchorSpacing;
        }

        // Create a scene
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const width = (mountRef.current.offsetWidth * 0.9) > 700 ? (mountRef.current.offsetWidth * 0.9) : 700;
        const height = (mountRef.current.offsetHeight * 0.9) > 700 ? (mountRef.current.offsetHeight * 0.9) : 700;

        // Create a camera
        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(2.2, 1.5, 3.1);
        cameraRef.current = camera;

        // Create a renderer
        const renderer = new THREE.WebGLRenderer({ alpha: true });
        // Set the size of the renderer to the size of its container
        renderer.setSize(width, height);
        rendererRef.current = renderer;

        // Append the renderer to the mount point
        mountRef.current.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);

        controls.enableRotate = false;
        controls.enableZoom = false;
        controls.minDistance = 3;
        controls.maxDistance = 5;

        document.getElementById('btnZoomIn')?.addEventListener("click", event => {
            tween('in', camera, controls);
          })
          
        document.getElementById('btnZoomOut')?.addEventListener("click", event => {
            tween('out', camera, controls);
        })

        document.getElementById('btnZoomReset')?.addEventListener("click", event => {
            tween('reset', camera, controls);
        })

        renderer.setClearColor(0x000000, 0);

        // Load the texture image
        const textureLoader = new THREE.TextureLoader();

        let scaledSlopeTotalHeight = calculateScaledValue(slopeTotalHeight, slopeTotalHeight);

        let scaledDebrisAccumulationHeight = calculateScaledValue(debrisAccumulationHeight, slopeTotalHeight);

        let scaledDebrisAccumulationWidth = calculateScaledValue(debrisAccumulationWidth, slopeTotalHeight);

        createRockSlopeShape(slopeInclination, scaledSlopeTotalHeight, scene, renderer, textureLoader);

        createRockSlopeBorder(scene, slopeInclination);

        createPlane(2, 1.5, calculateTouchPointX(slopeInclination), 0, 0.75, Math.PI / 2, scene, renderer,textureLoader);
        
        let scaledHorizontalSpacing = calculateScaledValue(horizontalSpacing, slopeTotalHeight);

        createAnchors(scene, renderer, textureLoader, scaledHorizontalSpacing);

        let netImage = selectNetTexture(committedProject.data?.mesh?.meshTypeName);

        createNet(scene, textureLoader, renderer, slopeInclination, scaledSlopeTotalHeight, debrisAccumulationHeight, scaledDebrisAccumulationHeight, debrisAccumulationWidth, scaledDebrisAccumulationWidth, slopeTotalHeight, netImage);

        //createNetPlane(slopeInclination, scaledSlopeTotalHeight, scaledDebrisAccumulationHeight, scaledDebrisAccumulationWidth, textureLoader, renderer, highestPoint, scene);

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            TWEEN.update();
            renderer.render(scene, camera);
            saveCanvasAsImage(renderer.domElement);
        };

        if (horizontalSpacing != 0 && slopeInclination != 0) {
            animate();
        }

        // Cleanup function
        return () => {
            mountRef.current?.removeChild(renderer.domElement);
        };
    }, [committedProject] );

    const saveCanvasAsImage = (domElement: HTMLCanvasElement) => {
        const canvas = domElement;
        if (canvas) {

            const src = canvas.toDataURL();
            // Save the image
            localStorage.setItem('graphic', src);
        }
    };

    const tween = (type: string, camera: THREE.PerspectiveCamera, controls: OrbitControls) => { // in - true, out - false
            
        let dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        dir.negate();
        let dist = controls.getDistance();

        let desiredDistance = 0;

        if(type === 'in'){
          desiredDistance = controls.minDistance;
        } else if(type === 'out'){
          desiredDistance = controls.maxDistance;
        } else if(type === 'reset'){
          desiredDistance = controls.minDistance + 1;
        }


        //console.log(dist, desiredDistance);
        
        new TWEEN.Tween({val: dist})
          .to({val: desiredDistance}, 1000)
          .onUpdate(val => {
            camera.position.copy(controls.target).addScaledVector(dir, val.val);
          })
          .start();
      }

    const calculateTouchPointX = (betaDegrees: number): number => {
        // Convert beta from degrees to radians
        const betaRadians = THREE.MathUtils.degToRad(betaDegrees);

        // Calculate x coordinate using the equation x = 1 / tan(beta)
        const xCoordinate = 1 / Math.tan(betaRadians);

        return xCoordinate;
    };

    const calculateScaledValue = (value: number, slopeTotalHeight: number): number => {
        let highestPoint = 1;
        let scalingFactor = highestPoint / slopeTotalHeight;
        return value * scalingFactor;
    };

    const getRockSlopeShape = (slopeTotalHeight: number, slopeInclination: number) => {
        const shape = new THREE.Shape();
        shape.moveTo(-0.5, 0);
        shape.lineTo(-0.5, 1);
        shape.lineTo(0, 1);
        shape.lineTo(calculateTouchPointX(slopeInclination), 0);
        shape.lineTo(0, 0);

        return shape;
    };

    const createRockSlopeShape = (slopeInclination: number, slopeTotalHeight: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {
       
        const shape = getRockSlopeShape(slopeTotalHeight, slopeInclination);

        // Define the extrusion settings
        const extrudeSettings = {
            steps: 1,
            depth: 1.5, // The depth of the extrusion
            bevelEnabled: false,
        };

        // Create the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const wallTextureMaterial = textureLoader.load(wallTexture, () => {
            wallTextureMaterial.wrapS = THREE.RepeatWrapping;
            wallTextureMaterial.wrapT = THREE.RepeatWrapping;
            wallTextureMaterial.repeat.set(4, 4); // Set the desired number of repetitions
            wallTextureMaterial.anisotropy = renderer.capabilities.getMaxAnisotropy();
            wallTextureMaterial.colorSpace = THREE.SRGBColorSpace;
        });

        const groundTextureMaterial = textureLoader.load(groundTexture, () => {
            groundTextureMaterial.wrapS = THREE.RepeatWrapping;
            groundTextureMaterial.wrapT = THREE.RepeatWrapping;
            groundTextureMaterial.repeat.set(4, 4); // Set the desired number of repetitions
            groundTextureMaterial.anisotropy = renderer.capabilities.getMaxAnisotropy();
            groundTextureMaterial.colorSpace = THREE.SRGBColorSpace;
        });

        // Create material with texture
        const wallMaterial = new THREE.MeshBasicMaterial({ map: wallTextureMaterial });
        const groundMaterial = new THREE.MeshBasicMaterial({ map: groundTextureMaterial });

        const mesh = new THREE.Mesh(geometry, [groundMaterial, wallMaterial, wallMaterial, wallMaterial, wallMaterial, wallMaterial]);

        // Add the mesh to your scene
        scene.add(mesh);
    };

    const createRockSlopeBorder = (scene: THREE.Scene, slopeInclination: number) => {
        //Create the line
        const points = [];
        points.push(new THREE.Vector3(-0.5, 1, 1.5));
        points.push(new THREE.Vector3(-0.5, 1, 0));
        points.push(new THREE.Vector3(0, 1, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 1.5));

        points.push(new THREE.Vector3(0, 1, 1.5));
        points.push(new THREE.Vector3(-0.5, 1, 1.5));
        points.push(new THREE.Vector3(-0.5, 0, 1.5));
        points.push(new THREE.Vector3(-0.5, 0, 0));
        points.push(new THREE.Vector3(-0.5, 1, 0));

        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({ color: 0x8B4513 }); // Brown color
        const line = new THREE.Line(lineGeometry, lineMaterial);

        scene.add(line);
    };

    const createPlane = (width: number, height: number, x: number, y: number, z: number, rotationX: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {

        const streetTexture = textureLoader.load(streetTextureImg, () => {
        
            streetTexture.wrapS = THREE.RepeatWrapping;
            streetTexture.wrapT = THREE.RepeatWrapping;
            streetTexture.repeat.set(2, 2); // Set the desired number of repetitions
            streetTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            streetTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const planeMaterial = new THREE.MeshBasicMaterial({ map: streetTexture, side: THREE.DoubleSide, transparent: false }); // Green color
        const planeGeometry = new THREE.PlaneGeometry(width, height);

        const plane = new THREE.Mesh(planeGeometry, planeMaterial);

        plane.position.x = x; // Half the depth of the extrusion
        plane.position.y = y; // Half the depth of the extrusion
        plane.position.z = z; // Half the depth of the extrusion
        plane.rotation.x = rotationX; // Rotate the plane to be horizontal

        scene.add(plane);
    };

    const approximateXFromY = (shape: THREE.Shape, targetY: number, tolerance = 0.01) => {

        const path = shape.extractPoints(1).shape;

        // Iterate through points in the path
        for (let i = 2; i < path.length - 1; i++) {
            const point1 = path[i];
            const point2 = path[i + 1];

            // Check if targetY falls within the vertical range of the current segment
            if (Math.min(point1.y, point2.y) <= targetY && Math.max(point1.y, point2.y) >= targetY) {
                // Interpolate x coordinate based on neighboring points
                const t = (targetY - point1.y) / (point2.y - point1.y);
                const interpolatedX = point1.x + t * (point2.x - point1.x);
                //console.log(interpolatedX, targetY)
                return interpolatedX;
            }
        }
    
        // If no matching segment found, return null or handle accordingly
        return null;
    };

    const calculateDebrisAccumulationAngle = (point1: THREE.Vector3, point2: THREE.Vector3, point3: THREE.Vector3) => {
        // Calculate lengths of sides
        const a = point2.distanceTo(point1);
        const b = point3.distanceTo(point2);
        const c = point1.distanceTo(point3);
    
        // Calculate cosines of angle
        const cosC = (a*a + b*b - c*c) / (2*a*b);
    
        // Calculate angle in radians
        const C = Math.acos(cosC);
    
        // Convert angle to degrees
        const C_deg = THREE.MathUtils.radToDeg(C);
    
        return  C_deg;
    };

    function calculateBd(Hd: number, Td: number, b: number) {
        // Convert b from degrees to radians
        let bInRadians = b * (Math.PI / 180);
    
        // Calculate 1 / tan(b)
        let invTanB = 1 / Math.tan(bInRadians);
    
        // Calculate the denominator Td + Hd * invTanB
        let denominator = Td + Hd * invTanB;
    
        // Calculate the division Hd / denominator
        let divisionResult = Hd / denominator;
    
        // Calculate the arctangent of the division result
        let bd = Math.atan(divisionResult);

        let bdInDegrees = bd * (180 / Math.PI);
    
        return parseFloat(bdInDegrees.toFixed(2));
    }

    const selectNetTexture = (meshTypeName?: string) => {
        //select the substring till the first space
        let meshType = meshTypeName?.split(' ')[0].toLocaleLowerCase();

        //console.log(meshType);

        switch (meshType) {
            case 'steelgrid':
                return steelGridMesh;
            case 'hea':
                return heaMesh;
            case 'dt':
                return dtMesh;
            case 'macring':
                return macringMesh;
            case 'macarmour':
                return macArmourMesh;
            default:
                return steelGridMesh;
        }
    }

    const createNet = (scene: THREE.Scene, textureLoader: THREE.TextureLoader,
        renderer: THREE.WebGLRenderer,   slopeInclination: number, scaledSlopeTotalHeight: number,
        debrisAccumulationHeight: number, scaledDebrisAccumulationHeight: number,
        debrisAccumulationWidth: number, scaledDebrisAccumulationWidth: number, slopeTotalHeight: number, netImage: string) => {

        let hdY = scaledDebrisAccumulationHeight;

        const debrisAccumulationAngle = calculateBd(scaledDebrisAccumulationHeight, scaledDebrisAccumulationWidth, slopeInclination);
        // Inside your Graphic component, after debrisAccumulationAngle has been calculated
        dispatch(setDebrisAccumulationAngle(debrisAccumulationAngle));
        //console.log(debrisAccumulationAngle);
        const rockSlopeShape = getRockSlopeShape(slopeTotalHeight, slopeInclination)
        
        let hdX = approximateXFromY(rockSlopeShape, hdY);

        const points = [];
        points.push(new THREE.Vector3(0.05, 1));
        points.push(new THREE.Vector3(0.05, 1));
        if (hdX !== null) {
            points.push(new THREE.Vector3(hdX, hdY));
            points.push(new THREE.Vector3(hdX, hdY));
        }
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination) + scaledDebrisAccumulationWidth, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination) + scaledDebrisAccumulationWidth, 0));
        if (hdX !== null) {
            points.push(new THREE.Vector3(hdX, hdY));
            points.push(new THREE.Vector3(hdX, hdY));
        }

        const shape = new THREE.Shape();
        shape.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            shape.lineTo(points[i].x, points[i].y);
        }
        shape.lineTo(points[0].x, points[0].y); // Close the shape

        // Define extrusion settings
        const extrudeSettings = {
            depth: 1.45, // Depth of the extrusion
            bevelEnabled: false, // Disable bevel for simplicity
            bevelSegments:10
        };

        // Create the geometry by extruding the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        //Move the geometry up by 0.05 to avoid z-fighting
        geometry.translate(0, 0, 0.05);

        let scale = netImage == macringMesh ? 6 : 8;

        const netTexture = textureLoader.load(netImage, () => {
        
            netTexture.wrapS = THREE.RepeatWrapping;
            netTexture.wrapT = THREE.RepeatWrapping;
            netTexture.repeat.set(scale, scale); // Set the desired number of repetitions
            netTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            netTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const material = new THREE.MeshBasicMaterial({ map: netTexture, side: THREE.DoubleSide, transparent: true }); // Green color

        // Create a mesh using the geometry and material
        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 1;

        // Add the mesh to the scene
        scene.add(mesh);
    };

    const createAnchor = (scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, x: number , y: number, z: number, scaledHorizontalSpacing: number) => {

        const anchorTexture = textureLoader.load(chiodoImg, () => {
            anchorTexture.wrapS = THREE.RepeatWrapping;
            anchorTexture.wrapT = THREE.RepeatWrapping;
            anchorTexture.repeat.set(1, 1); // Set the desired number of repetitions
            anchorTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            anchorTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const anchorMaterial = new THREE.MeshBasicMaterial({ map: anchorTexture, side: THREE.DoubleSide, transparent: true });

        let anchorSize = scaledHorizontalSpacing > 0.1 ? 0.1 : scaledHorizontalSpacing < 0.03 ? 0.03 : scaledHorizontalSpacing;

        const anchorGeometry = new THREE.PlaneGeometry(anchorSize, anchorSize); 

        const mesh = new THREE.Mesh(anchorGeometry, anchorMaterial);

        mesh.position.set(x, y, z);
        mesh.rotation.y = Math.PI / 2;
        mesh.renderOrder = 3;
        
        scene.add(mesh);
    }

    const createAnchors = (scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, scaledHorizontalSpacing: number) => {

        //console.log(scaledHorizontalSpacing);

        let x = 0.2;
        let y = 1;
        let z = 1.5;

        for (let i = 0; i < 100; i++) {
            if (i == 0) {
                createAnchor(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
                continue;
            }
    
            if ((z - scaledHorizontalSpacing) <= 0.3) { // Start a new row every time z is < 0
                break;
            } else {
                z -= scaledHorizontalSpacing; 
                createAnchor(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
            }
        }
    };

    return (
        <Grid container id='container' spacing={2} style={{ background: `repeat url(${gridImage})` }}>

            <Grid ref={mountRef} id='graphic' item md={8} xs={8} className='canvas-container'></Grid>

            <Grid item md={4} xs={4} container>

            <Grid item md={10} xs={10}></Grid>

            {!inResults && (
            <Grid item md={1} xs={1} id="zoom">
                <button type='button' id="btnZoomIn"><i className='fa-solid fa-plus'/> </button>
                <button id="btnZoomOut"><i className='fa-solid fa-minus' /></button>
                <button id="btnZoomReset"><i className='fa-solid fa-expand' /></button>
            </Grid>
             )}

               <Grid item id='legend'>
                <img src={graphicLegend} alt='legend' />
               </Grid>
            </Grid>
        </Grid>
    );
};

export default Graphic;
